import { datadog, tradeLogger } from '@flipster/utils/datadog';
import segment, { ChartEvent } from '@flipster/utils/segment';
import webviewHandler from '@flipster/utils/webview-handler';

import { TradingViewDataManager } from '../misc/data-manager';
import TradingView from '../trading-view';

const AutomatedDrawings = ['LineToolPosition', 'LineToolOrder', 'LineToolExecution'];

export function tvLogger(tv: TradingView, webview: boolean = false) {
  const { widget } = tv;
  datadog.addTiming('tv_logger_start');

  function chartLoaded() {
    // webview 만 체크하는게 맞을까 싶긴한데 기존 코드 유지
    if (!webview) return;
    datadog.addTiming('chart_loaded');
    tradeLogger.info('chart_data_loaded', {
      symbol: tv.symbol,
      chart_resolution: tv.activeChart?.resolution(),
    });
    webviewHandler.postMessage({ action: 'info', params: { message: 'chart_data_loaded' } });
  }

  datadog.addTiming('chart_loaded');
  // Use Map inside the function closure to avoid global state
  const studyInfoCache = new Map<string, { name: string }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function extractMetaInfoDescriptions(chartData: { panes?: any[] }): string[] | undefined {
    return chartData.panes
      ?.flatMap((pane) => pane.sources || [])
      .filter((source) => source.type === 'Study')
      .map((study) => study.metaInfo?.description)
      .filter((description) => description !== undefined);
  }

  const updateStudyCache = () => {
    try {
      const allStudies = widget?.activeChart?.()?.getAllStudies() || [];
      // Clear existing cache and repopulate with current studies
      studyInfoCache.clear();
      allStudies.forEach((study) => {
        if (study && study.id) {
          studyInfoCache.set(study.id, {
            name: study.name || 'Unknown Study',
          });
        }
      });
    } catch {
      // Ignore errors during cache update
    }
  };
  updateStudyCache();
  const cacheUpdateInterval = setInterval(updateStudyCache, 5000); // Update every 5 seconds
  // Clean up interval on widget unload if possible
  const originalUnload = window.onunload;
  window.onunload = function (e: BeforeUnloadEvent) {
    clearInterval(cacheUpdateInterval);
    if (typeof originalUnload === 'function') {
      return originalUnload.call(window, e);
    }
    return undefined;
  };

  widget?.subscribe('indicators_dialog', () => {
    segment.track(ChartEvent.IndicatorClick);
  });

  widget?.subscribe('study', (e) => {
    segment.track(ChartEvent.IndicatorManageClick, e);
    tradeLogger.info('chart_indicator_created', {
      symbol: tv.symbol,
      indicator_name: e.value,
    });

    updateStudyCache();
  });

  const studyRemovedCallMap: Record<string, number> = {};
  const timeoutMap: Record<string, NodeJS.Timeout> = {};

  widget?.subscribe('study_event', (entityId, eventType) => {
    if (eventType === 'remove') {
      try {
        const studyInfo = studyInfoCache.get(entityId.toString());
        const studyName = studyInfo?.name || 'Unknown Study';

        tradeLogger.info('chart_indicator_removed', {
          symbol: tv.symbol,
          indicator_id: entityId,
          indicator_name: studyName,
        });

        segment.track(ChartEvent.IndicatorRemoved, {
          indicator_id: entityId,
          indicator_name: studyName,
        });

        studyInfoCache.delete(entityId.toString());
      } catch {
        tradeLogger.info('chart_indicator_removed', {
          symbol: tv.symbol,
          indicator_id: entityId,
        });

        segment.track(ChartEvent.IndicatorRemoved, {
          indicator_id: entityId,
        });
      } finally {
        if (timeoutMap[entityId]) {
          clearTimeout(timeoutMap[entityId]);
        }
        studyRemovedCallMap[entityId] ??= 0;
        studyRemovedCallMap[entityId]++;
        if (studyRemovedCallMap[entityId] === 10) {
          tradeLogger.error('chart_indicator_error', {
            symbol: tv.symbol,
            indicator_id: entityId,
            error_category: 'runtime_errors',
            error_message: 'Chart indicator seems deleting recursively',
          });
        }
        timeoutMap[entityId] = setTimeout(() => {
          studyRemovedCallMap[entityId] = 0;
        }, 3000);
      }
    }
  });

  widget?.subscribe('drawing', (e) => {
    if (AutomatedDrawings.includes(e.value)) return;
    tradeLogger.info('chart_indicator_created', {
      symbol: tv.symbol,
      indicator_name: e.value,
    });
  });

  const { activeChart } = tv;

  // Reset indicator removal counters and timeouts
  // to prevent false positive chart_indicator_error during studies restoration
  const resetIndicatorRemovalTracking = () => {
    Object.values(timeoutMap).forEach((timeout) => clearTimeout(timeout));
    Object.keys(studyRemovedCallMap).forEach((key) => {
      studyRemovedCallMap[key] = 0;
    });
    Object.keys(timeoutMap).forEach((key) => {
      delete timeoutMap[key];
    });
  };

  let isSymbolChange = true;
  activeChart?.onSymbolChanged().subscribe(null, () => {
    isSymbolChange = true;
    resetIndicatorRemovalTracking();
  });

  activeChart?.onIntervalChanged().subscribe(null, (e) => {
    if (webview) tradeLogger.info('chart_interval_changed', { chart_resolution: e });
    resetIndicatorRemovalTracking();
  });

  activeChart?.onDataLoaded().subscribe(null, () => {
    // 여러번 timing 이 호출되면 앞에 기록이 사라지고 뒤에 시간이 측정됨 심볼이 변경되었을때 1회만 호출하기 위함
    if (isSymbolChange) {
      chartLoaded();
      isSymbolChange = false;
    }
  });
  datadog.addTiming('tv_logger_subscribe');

  Promise.all([
    TradingViewDataManager.getItem('interval'),
    TradingViewDataManager.getItem('chartType'),
    TradingViewDataManager.getItem('template'),
    TradingViewDataManager.getItem('positionsVisible'),
    TradingViewDataManager.getItem('TPSLVisible'),
    TradingViewDataManager.getItem('triggerOrderVisible'),
    TradingViewDataManager.getItem('historyIndicatorVisible'),
    TradingViewDataManager.getItem('whaleTrackerVisible'),
  ]).then(
    ([
      interval,
      chartType,
      template,
      positionsVisible,
      tpslVisible,
      triggerOrderVisible,
      historyIndicatorVisible,
      whaleTrackerVisible,
    ]) => {
      segment.track(ChartEvent.ChartStatusView, {
        symbol: tv.symbol,
        chart_timeframe: interval,
        chart_type: chartType,
        chart_indicator: extractMetaInfoDescriptions(template ?? {}) ?? [],
        chart_options: {
          positions: positionsVisible,
          tpsl: tpslVisible,
          trigger_order: triggerOrderVisible,
          history_indicator: historyIndicatorVisible,
          whale_tracker: whaleTrackerVisible,
        },
      });
    },
  );

  // tv button click event
  const iframe = document.querySelector<HTMLIFrameElement>(`#${TradingView.container} iframe`);
  const iframeDoc = iframe?.contentWindow?.document;
  if (!iframe || !iframeDoc) return;
  const percentageButton = iframeDoc.querySelector('[data-name="percentage"]');
  percentageButton?.addEventListener('click', () => {
    segment.track(ChartEvent.PercentageClick, { value: percentageButton.ariaPressed ?? '' });
  });
  const autoButton = iframeDoc.querySelector('[data-name="auto"]');
  autoButton?.addEventListener('click', () => {
    segment.track(ChartEvent.AutoClick, { value: autoButton.ariaPressed ?? '' });
  });
}
